import { appwriteConfig } from '../../config/environment';
import { functions } from './client';

/**
 * Error de una acción del backend.
 *
 * `code` es el código estable que devuelve la Function (`chat_not_found`,
 * `display_name_required`…). La interfaz traduce ese código; nunca muestra
 * este mensaje, que viene en inglés y es para la consola.
 */
export class CommandError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'CommandError';
    this.code = code;
  }
}

export function isCommandError(error: unknown): error is CommandError {
  return error instanceof CommandError;
}

/**
 * Ejecuta una acción del backend.
 *
 * Es el único camino de la aplicación hacia las Functions: ninguna superficie
 * llama a Appwrite por su cuenta. El backend sigue siendo la autoridad sobre
 * permisos y validaciones; esto solo transporta la petición y la respuesta.
 *
 * `functionId` elige a qué Function va la acción. El chat tiene la suya, así
 * que puede crecer sin engordar `management-command`.
 */
export async function runCommand<T>(
  action: string,
  data: Record<string, unknown> = {},
  functionId: string = appwriteConfig.managementFunctionId,
): Promise<T> {
  const execution = await functions.createExecution({
    functionId,
    body: JSON.stringify({ action, ...data }),
    async: false,
  });

  let value: Record<string, unknown>;
  try {
    value = JSON.parse(execution.responseBody || '{}') as Record<string, unknown>;
  } catch {
    throw new CommandError('Invalid response body.', 'invalid_response');
  }

  if (execution.status === 'failed' || typeof value.error === 'string') {
    const message = typeof value.error === 'string' ? value.error : 'Command failed.';
    const code = typeof value.code === 'string' ? value.code : String(execution.responseStatusCode);
    throw new CommandError(message, code);
  }

  return value as T;
}
